import React from 'react';
import { XCircle, CheckCircle2, Clock, Zap, AlertTriangle, ShieldCheck } from 'lucide-react';

export const ProblemSolution: React.FC = () => {
  return (
    <section className="py-20 md:py-28 bg-brand-surface/50 border-y border-brand-border relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-xs font-semibold uppercase tracking-wider text-brand-primary px-3 py-1 rounded-full bg-brand-primary/10 border border-brand-primary/20">
            O Gargalo do Criador
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-4 mb-5 tracking-tight">
            Por que criar um infoproduto completo é tão desgastante?
          </h2>
          <p className="text-base sm:text-lg text-brand-textMuted leading-relaxed">
            Ferramentas isoladas geram arquivos soltos e contraditórios. O InfoForge substitui a fragmentação por uma esteira industrial de engenharia de conteúdo.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {/* Card O Caminho Tradicional */}
          <div className="p-8 rounded-2xl bg-brand-bg/80 border border-rose-500/20 relative overflow-hidden group hover:border-rose-500/40 transition-all">
            <div className="absolute top-0 right-0 w-32 h-32 bg-rose-500/5 rounded-bl-full pointer-events-none" />
            
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-rose-500/10 text-rose-400 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">O Modelo Fragmentado</h3>
                <p className="text-xs text-brand-textSubtle">Ferramentas soltas, chats genéricos e retrabalho manual</p>
              </div>
            </div>

            <ul className="space-y-4 text-sm text-brand-textMuted">
              <li className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
                <span><strong>6 a 12 semanas de esforço manual:</strong> saltando entre editores de texto, ferramentas de design, geradores de copy e planilhas.</span>
              </li>
              <li className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
                <span><strong>Perda de consistência:</strong> a copy promete uma coisa, o ebook entrega outra e os exercícios não batem com o conteúdo.</span>
              </li>
              <li className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
                <span><strong>Textos genéricos e alucinações:</strong> o uso de chats comuns produz textos rasos, cheios de clichês e sem profundidade didática.</span>
              </li>
              <li className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
                <span><strong>Inferno da diagramação manual:</strong> horas perdidas tentando transformar texto bruto em PDFs legíveis e apresentáveis.</span>
              </li>
            </ul>

            <div className="mt-8 pt-6 border-t border-brand-border/60 flex items-center gap-2 text-xs text-rose-400/90 font-medium">
              <Clock className="w-4 h-4" />
              Resultado: projetos abandonados pela metade e perda de timing de mercado.
            </div>
          </div>

          {/* Card O Jeito InfoForge */}
          <div className="p-8 rounded-2xl bg-gradient-to-br from-brand-surface via-brand-surface to-brand-primary/10 border border-brand-primary/40 relative overflow-hidden group hover:border-brand-cyan/50 transition-all shadow-xl shadow-brand-primary/5">
            <div className="absolute top-0 right-0 w-32 h-32 bg-brand-cyan/10 rounded-bl-full pointer-events-none" />
            
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-brand-primary/20 text-brand-cyan flex items-center justify-center">
                <Zap className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">A Engenharia InfoForge</h3>
                <p className="text-xs text-brand-textSubtle">Esteira integrada guiada por arquitetura e tese central</p>
              </div>
            </div>

            <ul className="space-y-4 text-sm text-brand-text">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                <span><strong>Fluxo estruturado e veloz:</strong> pesquisa, matriz de competências, capítulos e ativos de campanha orquestrados em horas.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                <span><strong>DNA de Marca unificado:</strong> todos os entregáveis compartilham o mesmo público-alvo, tese e vocabulário técnico sem contradições.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                <span><strong>Humanizer & Fact-Check integrados:</strong> rigor contra alucinações conceituais e remoção ativa de jargões robóticos.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                <span><strong>Compilação física determinística:</strong> entrega direta de arquivos prontos (PDF diagramado, EPUB, roteiros de VSL, workbooks e ZIP).</span>
              </li>
            </ul>

            <div className="mt-8 pt-6 border-t border-brand-border/60 flex items-center gap-2 text-xs text-brand-cyan font-medium">
              <ShieldCheck className="w-4 h-4" />
              Resultado: infoproduto pronto para empacotamento com padrão institucional.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
